import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Minus, Plus, Search } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PostComposer from "@/components/PostComposer";
import SportIcon from "@/components/SportIcon";
import { BoardPost } from "@/components/MessageBoard";
import { SPORTS, DEFAULT_SPORT } from "@/lib/sports";
import { getSavedFilters } from "@/lib/filterCookies";
import { feedHeaderBg } from "@/components/FeedGlow";

const COUNT_OPTIONS = [3, 5, 10, 25, 50];
const ACCENT = "#c19a6b";
const STORE_KEY = "tsi-forum-sport";
const COLLAPSE_KEY = "tsi-collapse-forum";

function initialSport() {
    try {
        const saved = localStorage.getItem(STORE_KEY);
        if (saved && SPORTS.includes(saved)) return saved;
    } catch { /* ignore */ }
    // No history → default to the sport currently selected on the main page.
    const f = getSavedFilters({});
    return f && SPORTS.includes(f.sport) ? f.sport : DEFAULT_SPORT;
}

/**
 * Per-sport community forum shown on the Feed page. One forum per sport; the
 * sport is chosen from an icon dropdown and persists across visits. Posts,
 * replies, reactions and deletes reuse the shared board system on the backend.
 */
export default function BasketballChat() {
    const { user } = useAuth();
    const [sport, setSport] = useState(initialSport);
    const [sort, setSort] = useState("recent");
    const [limit, setLimit] = useState(5);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
    });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [composing, setComposing] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [search, setSearch] = useState("");
    const [searchOpen, setSearchOpen] = useState(false);
    const menuRef = useRef(null);
    const searchRef = useRef(null);

    useEffect(() => { if (searchOpen && searchRef.current) searchRef.current.focus(); }, [searchOpen]);

    useEffect(() => { try { localStorage.setItem(STORE_KEY, sport); } catch { /* ignore */ } }, [sport]);
    useEffect(() => { try { localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ } }, [collapsed]);

    useEffect(() => {
        if (!menuOpen) return;
        const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [menuOpen]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/forum/${encodeURIComponent(sport)}`, { params: { sort, limit, ...(search ? { q: search } : {}) } });
            setData(data);
        } catch {
            setData({ posts: [], can_post: false, can_moderate: false });
        } finally { setLoading(false); }
    }, [sport, sort, limit, search]);

    useEffect(() => { load(); }, [load]);

    const submitPost = async (payload) => {
        await api.post(`/forum/${encodeURIComponent(sport)}/posts`, payload);
        toast.success(`Posted to ${sport} Chat`);
        setComposing(false);
        load();
    };

    const deletePost = async (id) => {
        try {
            await api.delete(`/boards/posts/${id}`);
            setData((d) => ({ ...d, posts: d.posts.filter((p) => p.id !== id) }));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not delete");
        }
    };

    const posts = data?.posts || [];

    return (
        <div className="mt-10" data-testid="sport-forum">
            <div
                className="sticky z-30 -mx-3 md:-mx-4 px-3 md:px-4 py-2.5 flex items-center justify-between gap-3 mb-3 flex-wrap"
                style={{ top: "var(--tsi-sticky-top, 64px)", background: feedHeaderBg("sport") }}
                data-testid="sport-forum-header"
            >
                {/* Title with sport-selector dropdown + inline search */}
                <div className="flex items-center gap-2 min-w-0">
                <div className="relative" ref={menuRef}>
                    <button
                        type="button"
                        data-testid="forum-sport-toggle"
                        onClick={() => setMenuOpen((v) => !v)}
                        className="group font-display tracking-wide inline-flex items-center gap-2 text-3xl hover:opacity-90"
                    >
                        <SportIcon sport={sport} variant="line" size={22} className="shrink-0" style={{ color: ACCENT }} />
                        <span>{sport} Chat</span>
                        <ChevronDown className="h-5 w-5 text-[#a0a0a0] group-hover:text-white transition-colors" />
                    </button>
                    {menuOpen && (
                        <div
                            data-testid="forum-sport-menu"
                            className="absolute left-0 top-full mt-2 z-[1200] min-w-[220px] max-h-[60vh] overflow-y-auto bg-[#141414] border border-[#2a2a2a] shadow-xl py-1"
                        >
                            {SPORTS.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    data-testid={`forum-sport-option-${s.replace(/\s+/g, "-").toLowerCase()}`}
                                    onClick={() => { setSport(s); setMenuOpen(false); setComposing(false); setQuery(""); setSearch(""); setSearchOpen(false); }}
                                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${s === sport ? "bg-[#c19a6b]/15 text-white" : "text-[#cfcfcf] hover:bg-[#0f0f0f] hover:text-white"}`}
                                >
                                    <SportIcon sport={s} variant="line" size={18} className="shrink-0" style={{ color: s === sport ? ACCENT : "#a0a0a0" }} />
                                    <span>{s} Chat</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    type="button"
                    data-testid="forum-collapse-toggle"
                    onClick={() => setCollapsed((v) => !v)}
                    aria-label={collapsed ? "Expand chat posts" : "Collapse chat posts"}
                    className="inline-flex items-center justify-center h-6 w-6 shrink-0 hover:opacity-70 transition-opacity"
                    style={{ color: ACCENT }}
                >
                    {collapsed ? <Plus className="h-5 w-5" /> : <Minus className="h-5 w-5" />}
                </button>

                {/* Search — icon toggles an expanding field */}
                <button
                    type="button"
                    data-testid="forum-search-toggle"
                    onClick={() => setSearchOpen((v) => !v)}
                    aria-label="Search forum"
                    className={`inline-flex items-center justify-center h-9 w-9 shrink-0 border transition-colors ${searchOpen || search ? "border-[#c19a6b] text-[#c19a6b] bg-[#c19a6b]/10" : "border-[#2a2a2a] text-[#a0a0a0] hover:text-white"}`}
                >
                    <Search className="h-4 w-4" />
                </button>
                <form
                    onSubmit={(e) => { e.preventDefault(); setSearch(query.trim()); }}
                    data-testid="forum-search"
                    className={`overflow-hidden transition-all duration-300 ${searchOpen ? "w-52 opacity-100" : "w-0 opacity-0"}`}
                >
                    <input
                        ref={searchRef}
                        data-testid="forum-search-input"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Escape") { setSearchOpen(false); if (query || search) { setQuery(""); setSearch(""); } } }}
                        placeholder={`Search ${sport} Chat…`}
                        className="w-full h-9 px-3 bg-[#0f0f0f] border border-[#2a2a2a] text-white text-sm rounded-none focus:outline-none focus:border-[#c19a6b]"
                    />
                </form>
                </div>

                {/* Controls — New post (left of the row), then Recent/Top, then count */}
                <div className="flex items-center gap-3 flex-wrap">
                    {user && (
                        <button
                            type="button"
                            data-testid="forum-new-post"
                            onClick={() => setComposing((v) => !v)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#c19a6b]/60 bg-[#c19a6b]/10 text-[#c19a6b] hover:bg-[#c19a6b]/20 text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors"
                        >
                            <Plus className="h-3.5 w-3.5" /> New post
                        </button>
                    )}
                    <div className="inline-flex border border-[#2a2a2a]" data-testid="forum-sort-toggle">
                        <button type="button" data-testid="forum-sort-recent" onClick={() => setSort("recent")} className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${sort === "recent" ? "bg-[#c19a6b] text-white" : "text-[#a0a0a0] hover:text-white"}`}>Recent</button>
                        <button type="button" data-testid="forum-sort-top" onClick={() => setSort("top")} className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${sort === "top" ? "bg-[#c19a6b] text-white" : "text-[#a0a0a0] hover:text-white"}`}>Top</button>
                    </div>
                    <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                        <select
                            data-testid="forum-count-select"
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none focus:outline-none"
                        >
                            {COUNT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </label>
                </div>
            </div>

            {!collapsed && (<>
            <div className="mb-4 text-sm text-[#8a8a8a]">The main {sport.toLowerCase()} forum — ask questions, share news, and start a discussion with the whole community.</div>

            {/* New post composer */}
            {composing && user && (
                <div className="mb-5 border border-[#2a2a2a] bg-[#141414] rounded-xl p-4" data-testid="forum-composer">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">New post in {sport} Chat</div>
                        <button type="button" onClick={() => setComposing(false)} data-testid="forum-composer-cancel" className="text-xs uppercase tracking-wider text-[#a0a0a0] hover:text-white">Close</button>
                    </div>
                    <PostComposer
                        onSubmit={submitPost}
                        accent={ACCENT}
                        placeholder="Ask a question, share news, or start a discussion…"
                        showSubject
                        testidPrefix="forum-composer"
                    />
                </div>
            )}
            {!user && (
                <div className="mb-5 border border-[#2a2a2a] bg-[#141414] p-4 text-sm text-[#a0a0a0]" data-testid="forum-login-prompt">
                    Log in to post and reply in the forum.
                </div>
            )}

            {loading && !data ? (
                <div className="text-sm text-[#a0a0a0] py-8 text-center">Loading forum…</div>
            ) : posts.length === 0 ? (
                <div className="border border-[#2a2a2a] bg-[#141414] py-10 text-center" data-testid="forum-empty">
                    <div className="text-sm text-[#a0a0a0]">{search ? `No posts match "${search}" in ${sport} Chat.` : `No posts yet in ${sport} Chat — be the first to start a conversation.`}</div>
                </div>
            ) : (
                <div className="space-y-4" data-testid="forum-items">
                    {posts.map((p) => (
                        <BoardPost
                            key={p.id}
                            post={p}
                            accent={ACCENT}
                            barColor={ACCENT}
                            canModerate={!!data?.can_moderate}
                            onDelete={() => deletePost(p.id)}
                        />
                    ))}
                </div>
            )}
            </>)}
        </div>
    );
}
